import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { DonationForm } from '@feathr/blackbox';
import { ArchiveModal, toast } from '@feathr/components';
import { errorMessage } from '@feathr/hooks';

export interface IDonationFormDeleteModalProps {
  form: DonationForm;
  opened: boolean;
  onClose: () => void;
}

function DonationFormDeleteModal({
  form,
  opened,
  onClose,
}: Readonly<IDonationFormDeleteModalProps>): JSX.Element {
  const { t } = useTranslation();

  async function destroy(): Promise<void> {
    const name = form.name;
    try {
      await form.delete();
      toast(t('Deleted "{{- name}}".', { name }), { type: 'success' });
      onClose();
    } catch (error) {
      toast(
        t("Couldn't delete donation form. {{- message}}", {
          message: errorMessage(error, t),
        }),
        { type: 'error' },
      );
    }
  }

  return (
    <ArchiveModal
      alertTitle={t(
        'This will permanently remove the form and all its data. It will immediately be unpublished from any live locations.',
      )}
      alertType={'danger'}
      confirmationPhrase={t('DELETE')}
      method={'delete'}
      onClose={onClose}
      onConfirm={destroy}
      opened={opened}
      requireConfirmation={true}
      t={t}
      title={t('Delete donation form?')}
    />
  );
}

export default observer(DonationFormDeleteModal);
